// Pure market-row helpers, extracted from PublisherForm so the
// publisher-facing intake can produce the identical MarketRow[] shape without
// duplicating the dedupe / state-inference / channel-splitting rules.

import { inferStateFromDmaName } from '../inferStateFromDmaName';
import type { MarketRow, MarketPayload } from './types';

export function blankMarketRow(): MarketRow {
  return { dmaCode: '', dmaName: '', channels: '', coveredStates: [] };
}

export function appendBlankMarketRow(rows: MarketRow[]): MarketRow[] {
  return [...rows, blankMarketRow()];
}

export function updateMarketRow(
  rows: MarketRow[],
  index: number,
  patch: Partial<MarketRow>,
): MarketRow[] {
  return rows.map((m, i) => (i === index ? { ...m, ...patch } : m));
}

export function removeMarketRow(rows: MarketRow[], index: number): MarketRow[] {
  return rows.filter((_, i) => i !== index);
}

export function isDmaTakenByOtherRow(
  rows: MarketRow[],
  index: number,
  dmaCode: string,
): boolean {
  return rows.some((other, i) => i !== index && other.dmaCode === dmaCode);
}

/**
 * Apply a DMA selection to one row.
 *
 * A newly-picked DMA seeds its home state into the CHANNEL-WIDE list, so the
 * common single-market case needs no extra clicks. Replacing or clearing the
 * row's DMA UN-seeds the old one's home state again, unless another selected
 * row also infers it: a misclicked metro must not leave its state behind.
 * (A manually-picked state that happens to match the replaced metro's home
 * state is removed too; the picker is right there to re-add it, and silently
 * keeping misclick debris is the worse failure.) Other manually-added states
 * are never touched.
 *
 * Selecting a DMA another row already holds is a no-op (the <select> also
 * renders those options disabled, this is the guard behind it).
 */
export function selectDmaForRow(
  rows: MarketRow[],
  index: number,
  dma: { code: string; name: string } | null,
): MarketRow[] {
  const prev = rows[index];
  if (!prev) return rows;
  if (dma && isDmaTakenByOtherRow(rows, index, dma.code)) return rows;

  const withDma = updateMarketRow(
    rows,
    index,
    dma ? { dmaCode: dma.code, dmaName: dma.name } : { dmaCode: '', dmaName: '' },
  );

  // Un-seed the replaced metro's home state, unless another row keeps it.
  const prevAuto = prev.dmaName ? inferStateFromDmaName(prev.dmaName) : null;
  const keptByOtherRow =
    prevAuto != null
    && withDma.some(
      (r, i) => i !== index && r.dmaName && inferStateFromDmaName(r.dmaName) === prevAuto,
    );
  let states = flatStatesOf(withDma);
  if (prevAuto && !keptByOtherRow) states = states.filter((s) => s !== prevAuto);

  const autoState = dma ? inferStateFromDmaName(dma.name) : null;
  if (autoState && !states.includes(autoState)) states = [...states, autoState];
  return applyStatesToAll(withDma, states);
}

/**
 * The channel-wide covered-state list.
 *
 * `coveredStates` is stored per market, but nothing reads it that way: the
 * wizard's suggested-state chips flatten it (`markets.flatMap(m =>
 * m.coveredStates)` in CampaignSetupStep) and the station center label takes
 * `markets[0].coveredStates[0]`. So both forms present one picker for the whole
 * channel and write the same list to every market, which is the only shape
 * those two consumers can actually distinguish.
 */
export function flatStatesOf<T extends { coveredStates: string[] }>(rows: T[]): string[] {
  const seen = new Set<string>();
  for (const r of rows) for (const s of r.coveredStates) seen.add(s);
  return Array.from(seen);
}

/** Write one state list onto every market, preserving the nested wire shape. */
export function applyStatesToAll<T extends { coveredStates: string[] }>(
  rows: T[],
  coveredStates: string[],
): T[] {
  return rows.map((r) => ({ ...r, coveredStates }));
}

/** Drop incomplete rows and split the free-text channel list into an array. */
export function toMarketsPayload(rows: MarketRow[]): MarketPayload[] {
  return rows
    .filter((m) => m.dmaCode && m.dmaName)
    .map((m) => ({
      dmaCode: m.dmaCode.trim(),
      dmaName: m.dmaName.trim(),
      channels: m.channels.split(',').map((c) => c.trim()).filter(Boolean),
      coveredStates: m.coveredStates,
    }));
}
